/**
 * VUE3-SPECIFIC
 */
import { createApp, defineAsyncComponent } from 'vue/dist/vue.esm-bundler.js';
import LoadingDots from "../loader/LoadingDots.vue";
import HermeneusIcon from "../hermeneus.icons/HermeneusIcon.vue";
import IrisMessengerComponent from "../iris-messenger/components/IrisMessenger.vue";
import WebcodeNavigator from "../hermeneus.webcode-navigator/WebcodeNavigator.vue";
import Flash from "../iris-messenger/components/Flash.vue";
import LoadMore from "../load-more/LoadMore.vue";
import NaviMobile from "../hermeneus.navi/NaviMobile.vue";
import AuthNavi from "../hermeneus.menu/AuthMenu.vue";
import HermeneusCollapsible from "../hermeneus.collapsible/HermeneusCollapsible.vue";
import LoadingDualRing from "../loader/LoadingDualRing.vue";
import LoadingDoubleBounce from "../loader/LoadingDoubleBounce.vue";
import { createPinia, PiniaVuePlugin } from 'pinia';


/**
 * MIXINS
 */
import subscriptionFeature from "./directives/subscriptionFeature.js";
import alphaFeature from "../hermeneus.vue-instance/directives/alphaFeature.js";
import advancedFeature from "../hermeneus.vue-instance/directives/advancedFeature.js";
import versionFeature from "./directives/versionFeature.js";
import aiCost from "./directives/aiCost.js";
import ModalOpener from "../hermeneus-modal/ModalOpener.vue";
import RootComponent from "../hermeneus.vue-instance/components/RootComponent.vue";
import NaviDesktop from "../hermeneus.navi/NaviDesktop.vue";
import { providesUserDataPlugin } from "../hermeneus.vue-instance/plugins/providesUserData.plugin.js";
import { providesUebungenRegistryPlugin } from "../hermeneus.vue-instance/plugins/providesUebungenRegistry.plugin.js";
import { providesModuleRegistryPlugin } from "../hermeneus.vue-instance/plugins/providesModuleRegistry.plugin.js";
import { providesAppVersionPlugin } from "./plugins/providesAppVersion.plugin.js";

import Reader from "../hermeneus.reader/js/components/Reader.vue";
import HermeneusMarkdownWrapper from "../hermeneus.markdown-wrapper/HermeneusMarkdownWrapper.vue";
/**
 * PLUGINS: THIRD PARTY
 */
import Buefy from "@ntohq/buefy-next";

import VueTippy from "vue-tippy";
import VueShepherdPlugin from "vue-shepherd";
import "tippy.js/themes/light.css";
import JsonEditorVue from 'json-editor-vue'
import PrimeVue from 'primevue/config';
import { usePassThrough } from "primevue/passthrough";
import HProgressor from "../components/h-progressor/HProgressor.vue";
/*import hermeneus from "../primevue/presets/hermeneus";*/
import { useIrisMessengerStore } from "../iris-messenger/IrisMessengerStore.js";
import LoadingComet from "../loader/LoadingComet.vue";
import Divider from 'primevue/divider';
import Checkbox from "primevue/checkbox";
import ProgressBar from "primevue/progressbar";
import Paginator from "primevue/paginator";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import ButtonGroup from 'primevue/buttongroup';
import InputNumber from "primevue/inputnumber";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from 'primevue/inputgroupaddon';
import Textarea from "primevue/textarea";
import Tooltip from "primevue/tooltip";
import Popover from "primevue/popover";
import PrimevueHermeneusPreview from "@/primevue/PrimevueHermeneusPreview.vue";
import GermanLocale from "@/primevue/presets/hermeneus/locale_de.json";
import Radiobutton from "primevue/radiobutton";
import Datepicker from 'primevue/datepicker';
import ToggleSwitch from 'primevue/toggleswitch';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Rating from 'primevue/rating';
import Skeleton from 'primevue/skeleton';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import Fieldset from 'primevue/fieldset';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import Slider from 'primevue/slider';
import Tabs from 'primevue/tabs';
import InputText from 'primevue/inputtext';
import Aura from '@primevue/themes/aura';
import HermeneusPrimevuePreset from "../primevue/presets/hermeneus/hermeneus.js";
import LoginForm from "@/auth/LoginForm.vue";
import LoginFormMin from "@/auth/LoginFormMin.vue";
import {Card, Listbox, MeterGroup, Toolbar} from "primevue";
import Editor from 'primevue/editor';

const GlobalVue = createApp(RootComponent);

GlobalVue.config.errorHandler = function (err, vm, info) {
    console.error(err, vm, info);
}


GlobalVue.component('wortart-adjektiv', defineAsyncComponent(() => import("../wortarten/WortartAdjektiv.vue")));
GlobalVue.component('wortart-eigenname', defineAsyncComponent(() => import("../wortarten/WortartEigenname.vue")));
GlobalVue.component('wortart-nomen', defineAsyncComponent(() => import("../wortarten/WortartNomen.vue")));
GlobalVue.component('wortart-numerale', defineAsyncComponent(() => import("../wortarten/WortartNumerale.vue")));
GlobalVue.component('wortart-partikel', defineAsyncComponent(() => import("../wortarten/WortartPartikel.vue")));
GlobalVue.component('wortart-pronomen', defineAsyncComponent(() => import("../wortarten/WortartPronomen.vue")));
GlobalVue.component('wortart-verb', defineAsyncComponent(() => import("../wortarten/WortartVerb.vue")));
GlobalVue.component('wortart-wendung', defineAsyncComponent(() => import("../wortarten/WortartWendung.vue")));


/**
 *
 */
GlobalVue.component('morph-table', defineAsyncComponent(() => import("../wortarten/MorphTable.vue")));
GlobalVue.component('morph-table-verb', defineAsyncComponent(() => import("../wortarten/MorphTables/MorphTableVerb.vue")));
GlobalVue.component('vocab-composer', defineAsyncComponent(() => import("../vocab-composer/VocabComposer.vue")));
GlobalVue.component('vokabellisten-library', defineAsyncComponent(() => import("../vokabellisten-library/VokabellistenLibrary.vue")));
GlobalVue.component('vokabellisten-creation-manager', defineAsyncComponent(() => import("../vokabellisten-creator/VokabellistenCreationManager.vue")));
GlobalVue.component('vokabellisten-editor', defineAsyncComponent(() => import("../vokabellisten-editor/VokabellistenEditor.vue")));
GlobalVue.component('vocab-analysis-scope-viewer', defineAsyncComponent(() => import("../vocab-analysis-scope-viewer/VocabAnalysisScopeViewer.vue")));
GlobalVue.component('grammatik-bestimmungen-selector', defineAsyncComponent(() => import("../grammatik-kategorien/GrammatikBestimmungenSelector.vue")));
GlobalVue.component('morpho-blocks', defineAsyncComponent(() => import("../grammatik-kategorien/MorphoBlocks.vue")));
GlobalVue.component('morpho-mapper', defineAsyncComponent(() => import("../morphomapper.js/vue/MorphoMapper.vue")));
/**
 * VOKABELLISTEN
 */
/**
 * GLOBAL COMPONENTS
 */

GlobalVue.component('loading-dots', LoadingDots);
GlobalVue.component('loading-double-bounce', LoadingDoubleBounce);
GlobalVue.component('loading-dual-ring', LoadingDualRing);
GlobalVue.component('loading-comet', LoadingComet);
GlobalVue.component('load-more', LoadMore);
GlobalVue.component('hermeneus-icon', HermeneusIcon);
GlobalVue.component('iris-messenger', IrisMessengerComponent);
GlobalVue.component('flash', Flash);
GlobalVue.component('modal-opener', ModalOpener);
GlobalVue.component('webcode-navigator', WebcodeNavigator);
GlobalVue.component('hermeneus-collapsible', HermeneusCollapsible);
GlobalVue.component('hermeneus-markdown-wrapper', HermeneusMarkdownWrapper);
GlobalVue.component('h-progressor', HProgressor);

/**
 * AUTH / PROFILE
 */
GlobalVue.component('profile', defineAsyncComponent(() => import("../hermeneus.profile/HermeneusProfile.vue")));
GlobalVue.component('hermeneus-newsletter-activator', defineAsyncComponent(() => import("../hermeneus.profile/HermeneusNewsletterActivator.vue")));
GlobalVue.component('share-modal', defineAsyncComponent(() => import("../hermeneus.share/ShareModal.vue")));
GlobalVue.component('request-user-role', defineAsyncComponent(() => import("../request-user-role/RequestUserRole.vue")));
GlobalVue.component('hermeneus-contact', defineAsyncComponent(() => import("../hermeneus.contact/HermeneusContact.vue")));
GlobalVue.component('gravitas-counter', defineAsyncComponent(() => import("../hermeneus.gravitas-counter/GravitasCounter.vue")));
GlobalVue.component('hermeneus-tester-tickets-jira', defineAsyncComponent(() => import("../hermeneus.tickets-jira/HermeneusTesterTicketsJira.vue")));


/**
 * GLOSSARIUM
 */
GlobalVue.component('glossarium', defineAsyncComponent(() => import("../glossarium/Glossarium.vue")));
GlobalVue.component('glossarium-vocab-creator', defineAsyncComponent(() => import("../glossarium/GlossariumVocabCreator.vue")));
GlobalVue.component('glossarium-vocab-creator-suggest', defineAsyncComponent(() => import("../glossarium/GlossariumVocabCreatorSuggest.vue")));
GlobalVue.component('glossarium-vocab-suggestion-verifier', defineAsyncComponent(() => import("../glossarium/GlossariumVocabSuggestionVerifier.vue")));
GlobalVue.component('glossarium-vocab-creator-finished-loader', defineAsyncComponent(() => import("../glossarium/GlossariumVocabCreatorFinishedLoader.vue")));
GlobalVue.component('vocab-viewer', defineAsyncComponent(() => import("../vocab-viewer/VocabViewer.vue")));
GlobalVue.component('vocab-creator', defineAsyncComponent(() => import("../vocab-creator/VocabCreator.vue")));
GlobalVue.component('vocab-editor', defineAsyncComponent(() => import("../vocab-editor/VocabEditor.vue")));
GlobalVue.component('glossarium-quicksearch', defineAsyncComponent(() => import("../glossarium.quicksearch/GlossariumQuicksearch.vue")));

/**
 * ÜBUNGEN (EXTRACT)
 */
GlobalVue.component('uebungen-library', defineAsyncComponent(() => import("../uebungen-library/UebungenLibrary.vue")));
GlobalVue.component('uebungen-library-max-gravitas-level-selector', defineAsyncComponent(() => import("../uebungen-library/components/ScoringComponents/UebungenLibraryMaxGravitasLevelSelector.vue")));
GlobalVue.component('uebungen-library-avg-time-seconds-selector', defineAsyncComponent(() => import("../uebungen-library/components/ScoringComponents/UebungenLibraryAvgTimeSecondsSelector.vue")));

GlobalVue.component('uebung-creator', defineAsyncComponent(() => import("../uebung-creator/UebungCreator.vue")));
GlobalVue.component('uebung-show', defineAsyncComponent(() => import("../uebung-show/UebungShow.vue")));
GlobalVue.component('uebung-print', defineAsyncComponent(() => import("../uebung-print/UebungPrint.vue")));


/**
 * LERNINHALTE (EXTRACT)
 */
GlobalVue.component('lemma-composer', defineAsyncComponent(() => import("../lemma-composer/LemmaComposer.vue")));
GlobalVue.component('lerninhalte-picker', defineAsyncComponent(() => import("../lerninhalte-picker/LerninhaltePicker.vue")));
GlobalVue.component('lerneinheiten-library', defineAsyncComponent(() => import("../lerneinheiten-library/LerneinheitenLibrary.vue")));
GlobalVue.component('lerneinheiten-editor', defineAsyncComponent(() => import("../lerneinheiten-editor/LerneinheitenEditor.vue")));
GlobalVue.component('lerneinheiten-creator', defineAsyncComponent(() => import("../lerneinheiten-editor/LerneinheitenCreator.vue")));
GlobalVue.component('lerneinheiten-selector', defineAsyncComponent(() => import("../lerneinheiten.selector/LerneinheitenSelector.vue")));
GlobalVue.component('grammatik-selector', defineAsyncComponent(() => import("../grammatik.selector/GrammatikSelector.vue")));

GlobalVue.component('grammatik-library', defineAsyncComponent(() => import("../grammatik-library/GrammatikLibrary.vue")));
GlobalVue.component('grammatik-library-edit', defineAsyncComponent(() => import('../grammatik-library/GrammatikLibraryEdit.vue')));
GlobalVue.component('grammatik-library-create', defineAsyncComponent(() => import('../grammatik-library/GrammatikLibraryCreate.vue')));
GlobalVue.component('lerneinheiten-vocab-importer', defineAsyncComponent(() => import("../lerneinheiten-vocab-importer/LerneinheitenVocabImporter.vue")));

/**
 * TEXTE (EXTRACT)
 */
GlobalVue.component('text-library', defineAsyncComponent(() => import("../text-library/TextLibrary.vue")));
GlobalVue.component('textconfig-editor', defineAsyncComponent(() => import("../textconfig-editor/TextconfigEditor.vue")));
GlobalVue.component('text-publish-button', defineAsyncComponent(() => import("../hermeneus.text-publish-button/TextPublishButton.vue")));
GlobalVue.component('text-copy-dialog', defineAsyncComponent(() => import("../hermeneus.text-copy-dialog/TextCopyDialog.vue")));
GlobalVue.component('vocab-extraction', defineAsyncComponent(() => import("../text-tools/vocab-extraction/VocabExtraction.vue")));
GlobalVue.component('textverwaltung', defineAsyncComponent(() => import("../textverwaltung/Textverwaltung.vue")));
/**
 * HERMENEUS-READER
 */
GlobalVue.component('hermeneus-reader', Reader);

/**
 * TEXTAUFBEREITUNG
 */
GlobalVue.component('text-aufbereitung', defineAsyncComponent(() => import("../text-creator/components/TextAufbereitung.vue")));


/**
 * VOKABELHEFT
 */
GlobalVue.component('vokabelheft', defineAsyncComponent(() => import("../hermeneus.vokabelheft/Vokabelheft.vue")));


/**
 * Administration
 */
GlobalVue.component('admin-panel', defineAsyncComponent(() => import("../administration/AdminPanel.vue")));

/**
 * GRUPPENVERWALTUNG
 */
GlobalVue.component('gruppenverwaltung', defineAsyncComponent(() => import("../gruppenverwaltung/Gruppenverwaltung.vue")));
GlobalVue.component('gruppenverwaltung-gruppe', defineAsyncComponent(() => import("../gruppenverwaltung/GruppenverwaltungGruppe.vue")));

/**
 * TICKETS
 *
 */
GlobalVue.component('hermeneus-tickets-jira', defineAsyncComponent(() => import("../hermeneus.tickets-jira/HermeneusTicketsJira.vue")));

/**
 * REDAKTION
 */
GlobalVue.component('code-management', defineAsyncComponent(() => import("../code-management/CodeManagement.vue")));
GlobalVue.component('buecher-library', defineAsyncComponent(() => import("../buecher-library/BuecherLibrary")));
GlobalVue.component('buecher-library-buch', defineAsyncComponent(() => import("../buecher-library/BuecherLibraryBuch")));
GlobalVue.component('buecher-library-create', defineAsyncComponent(() => import("../buecher-library/BuecherLibraryCreate")));


/**
 * ZUSATZINHALTE
 */
GlobalVue.component('zusatzinhalte', defineAsyncComponent(() => import("../zusatzinhalte/Zusatzinhalte.vue")));


/*const PresetConfigs = usePassThrough(hermeneus)*/

// PRIMEVUE
GlobalVue.use(PrimeVue, {
    theme: {
        preset: HermeneusPrimevuePreset,
        options: {
            cssLayer: {
                name: 'primevue',
                order: 'tailwind-base, primevue, tailwind-utilities'
            }
        }
    },
    locale: GermanLocale.de,
    /*pt: PresetConfigs,*/
    ptOptions: {
        mergeSections: false,
        mergeProps: true
    }

});
/*
* PRIMEVUE COMPONENTS
* Erstmal belassen, um zu sehen, welche Komponenten bereits angepasst wurden
 */
GlobalVue.component('primevue-hermeneus-preview', PrimevueHermeneusPreview)
GlobalVue.component('Divider', Divider)
GlobalVue.component('InputNumber', InputNumber)
GlobalVue.component('Paginator', Paginator)
GlobalVue.component('Datepicker', Datepicker)
GlobalVue.component('Fieldset', Fieldset)
GlobalVue.component('ToggleSwitch', ToggleSwitch)
GlobalVue.component('Button', Button)
GlobalVue.component('ButtonGroup', ButtonGroup)
GlobalVue.component('Checkbox', Checkbox)
GlobalVue.component('Dialog', Dialog)
GlobalVue.component('Accordion', Accordion)
GlobalVue.component('AccordionTab', AccordionTab)
GlobalVue.component('AccordionPanel', AccordionPanel)
GlobalVue.component('AccordionHeader', AccordionHeader)
GlobalVue.component('AccordionContent', AccordionContent)
GlobalVue.component('Select', Select)
GlobalVue.component('MultiSelect', MultiSelect)
GlobalVue.component('RadioButton', Radiobutton)
GlobalVue.component('ProgressBar', ProgressBar)
GlobalVue.component('InputGroup', InputGroup)
GlobalVue.component('InputGroupAddon', InputGroupAddon)
GlobalVue.component('Rating', Rating)
GlobalVue.component('Skeleton', Skeleton)
GlobalVue.component('Stepper', Stepper)
GlobalVue.component('StepList', StepList)
GlobalVue.component('StepPanels', StepPanels)
GlobalVue.component('StepItem', StepItem)
GlobalVue.component('Step', Step)
GlobalVue.component('StepPanel', StepPanel)
GlobalVue.component('Tabs', Tabs)
GlobalVue.component('Textarea', Textarea)
GlobalVue.component('Slider', Slider)
GlobalVue.component('InputText', InputText)
GlobalVue.component('Popover', Popover)
GlobalVue.component('Listbox', Listbox)
GlobalVue.component('Card', Card)
GlobalVue.component('MeterGroup', MeterGroup)
GlobalVue.component('Toolbar', Toolbar)
GlobalVue.component('Editor', Editor)

// PV Direktiven
GlobalVue.directive('tooltip', Tooltip)


GlobalVue.use(VueTippy);
GlobalVue.use(JsonEditorVue);
GlobalVue.use(Buefy, {
    defaultIconPack: '',
});

GlobalVue.use(VueShepherdPlugin);
GlobalVue.use(PiniaVuePlugin);
/**
 * PLUGINS: EIGENE
 */
GlobalVue.use(providesUserDataPlugin);
GlobalVue.use(providesUebungenRegistryPlugin);
GlobalVue.use(providesModuleRegistryPlugin);
GlobalVue.use(providesAppVersionPlugin);

/**
 * STORE-INTEGRATION
 */
const pinia = createPinia();
// Plugin um den GlobalVue in den Store zu injizieren
pinia.use(({store}) => {store.$GlobalVue = GlobalVue})
GlobalVue.use(pinia);
/**
 * IRIS MESSENGER
 */
GlobalVue.config.globalProperties.$IrisMessenger = useIrisMessengerStore();
window.$IrisMessenger = useIrisMessengerStore();
/**
 * HERMENEUS DIRECTIVES
 */

GlobalVue.directive('subscription-feature', subscriptionFeature);
GlobalVue.directive('alpha-feature', alphaFeature);
GlobalVue.directive('advanced-feature', advancedFeature);
GlobalVue.directive('version', versionFeature);
GlobalVue.directive('ai-cost', aiCost);

/**
 * NAVIGATION
 */
GlobalVue.component('navi-mobile', NaviMobile);
GlobalVue.component('auth-menu', AuthNavi);
GlobalVue.component('navi-desktop', NaviDesktop);

/**
 * AUTHENTICATION
 */
GlobalVue.component('login-form', LoginForm);
GlobalVue.component('login-form-min', LoginFormMin);


/**
 * HERMENEUS-MODULE INITIALISIEREN
 * Hier werden modulspezifische Komponenten registriert
 */
async function loadModules(VueAppInstance) {
    if (!VueAppInstance) {
        console.error('Vue-Instanz ist nicht verfügbar');
        return;
    }

    const moduleFiles = import.meta.glob('../../../modules/*/*/resources/index.js');
    for (const path in moduleFiles) {
        try {
            const module = await moduleFiles[path]();
            if (module.initModule && typeof module.initModule === 'function') {
                console.log(`✅ Modul geladen: %c${path}`, 'color: lime;');
                await module.initModule(VueAppInstance); // Initialisierung des Moduls
            }
        } catch (error) {
            console.error(`Fehler beim Laden des Moduls ${path}:`, error);
        }
    }
}

// Warte auf das Laden der Module, bevor die App gemountet wird
const modulesLoadedPromise = loadModules(GlobalVue);

/**
 * Vue-Directive: v-module-exists
 */
GlobalVue.directive('module-exists', (el, binding) => {
    /**
     * Wenn das Modul nicht existiert, wird es ausgeblendet.
     */
    if (!GlobalVue.config.globalProperties.$moduleExists(binding.value)) {
        el.style.display = 'none';
        el.style.pointerEvents = 'none';
        el.disabled = true;
        console.log(`❌ Modul ${binding.value} nicht gefunden. Gefordert von %c${binding.instance.$options.name}.`, 'color: red;');

    }
});


/**
 * Vue-Directive: v-modules-exist
 */
GlobalVue.directive('modules-exist', (el, binding) => {
    /**
     * Wenn das Modul nicht existiert, wird es ausgeblendet.
     */
    if (!GlobalVue.config.globalProperties.$modulesExist(binding.value)) {
        el.style.display = 'none';
        el.style.pointerEvents = 'none';
        el.disabled = true;
        console.log(`❌ Eines der Module [${binding.value}] nicht gefunden. Gefordert von %c${binding.instance.$options.name}.`, 'color: red;');

    }
});


/**
 * MOUNTING
 * Warte auf das Laden aller Module, bevor die App gemountet wird
 */

modulesLoadedPromise.then(() => {
    GlobalVue.mount('#vue_root');
    window.GlobalVue = GlobalVue;
    console.log('✅ Vue App gemountet nach dem Laden aller Module');
}).catch(error => {
    console.error('❌ Fehler beim Laden der Module:', error);
    // Mount trotzdem, damit die App nicht komplett fehlschlägt
    GlobalVue.mount('#vue_root');
    window.GlobalVue = GlobalVue;
});